import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory, useLocation } from 'react-router-dom';
import fetchFleetStatus from '../../libs/fleetStatus';
import fetchFridgeLocations from '../../libs/fridgeLocations';
import { isHistoryStale, formatGap } from '../../libs/heartbeatHistory';
import Loader from '../../components/atoms/Loader';
import DateRangeField from '../../components/atoms/forms/DateRangeField';
import MultiSelect from '../../components/atoms/forms/MultiSelect';
import {
  MULTI_FILTERS,
  DIRECT_MULTI_FILTERS,
  BOOL_FILTERS,
  emptyFilters,
  filtersToSearch,
  searchToFilters,
  searchTerms,
  matchesSearch,
  MATCH_EXACT,
  sortToParams,
  searchToSort,
} from './fleetStatusFilters';
import { isPlaceholderIp } from '../../utils/ipAddress';
import emaintWorkOrderUrl from '../../utils/emaint';
import theme from '../../theme';
import {
  FLEET_STATUS,
  STATUS_LABELS,
  INSTALL_AGE,
  INSTALL_AGE_LABELS,
  ONLINE_PRESET_LABELS,
  describeDateBounds,
  peripheralsDown,
  cleanVital,
  formatRelativeTime,
  installAge,
  workOrderOpenedAt,
  workOrderShortDesc,
  matchesOnlineRange,
  onlineDatePresets,
  parseLocalDate,
  localDateString,
} from './fleetStatusUtils';
import downloadFleetStatusCsv from './fleetStatusCsv';
import StyledDiv from './styles';
import CopyIcon from '../../assets/CopyIcon';
import CheckIcon from '../../assets/CheckIcon';
import HeartbeatSparkline from './HeartbeatSparkline';
import FleetHeartbeatTrend from './FleetHeartbeatTrend';

const STATUS_COLORS = {
  [FLEET_STATUS.UP]: theme.colors.primary,
  [FLEET_STATUS.DOWN]: theme.colors.danger,
  [FLEET_STATUS.INACCESSIBLE]: theme.colors.warning,
  // Grey, not amber: an absence of signal isn't a problem to go fix.
  [FLEET_STATUS.NO_DATA]: theme.colors.disabled,
  // A second grey, one step darker. Also an absence, but this one has a name
  // and an owner, so it should not read as quite the same shrug as NO_DATA.
  [FLEET_STATUS.NO_IP]: theme.colors.textMuted,
};

// Derived from the labels so a status can't appear in the dropdown under one name
// and in the table under another. Sorted by label, like every other filter — the
// cards and pills keep STATUS_LABELS' severity order.
const STATUS_OPTIONS = Object.entries(STATUS_LABELS)
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

// The three statuses the evaluator can actually assign, in severity order —
// unlike STATUS_OPTIONS, which is alphabetical for the dropdown. NO_DATA is not
// here: it has no percentage, so its card is built separately.
const EVALUATED_STATUS_ORDER = [
  FLEET_STATUS.UP,
  FLEET_STATUS.DOWN,
  FLEET_STATUS.INACCESSIBLE,
];

// The two absences, in their own cards. Neither has a percentage — they sit
// outside the evaluated denominator, so a share of it would mean nothing.
const ABSENT_STATUS_ORDER = [FLEET_STATUS.NO_DATA, FLEET_STATUS.NO_IP];

// Card tooltips. The card NAMES come from STATUS_LABELS
const STATUS_HINTS = {
  [FLEET_STATUS.UP]: 'Reachable and healthy',
  [FLEET_STATUS.DOWN]: 'Reachable but impaired',
  [FLEET_STATUS.INACCESSIBLE]: 'Evaluated and could not be reached',
};

const ABSENT_HINTS = {
  [FLEET_STATUS.NO_DATA]:
    'Fridge Health Monitoring never evaluated these fridges. Sort or filter ' +
    'by Stocking to find locations that should be reporting.',
  [FLEET_STATUS.NO_IP]:
    'No address in CMS to reach, so nothing about these fridges can be ' +
    'measured. Set an IP on the location to bring one under monitoring.',
};

// Tri-state for boolean facets: '' = Any, 'yes', 'no'.
// Not reusing BOOL_OPTIONS: "Yes"/"No" beside a Work orders label reads as a
// question about the column, not about what is open.
const WORK_ORDER_OPTIONS = [
  { value: 'yes', label: 'Has open work orders' },
  { value: 'no', label: 'No open work orders' },
];

const BOOL_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

// Each fridge peripheral — filters to fridges where that one is disconnected.
const PERIPHERAL_OPTIONS = [
  { value: 'cardReader', label: 'Card reader' },
  { value: 'electricalLayout', label: 'Electrical layout' },
  { value: 'elo', label: 'ELO' },
  { value: 'hdmi', label: 'HDMI' },
];

const INSTALL_BADGE_CLASS = {
  [INSTALL_AGE.UPCOMING]: 'fs-install-chip fs-install-chip--upcoming',
  [INSTALL_AGE.NEW]: 'fs-install-chip fs-install-chip--new',
};

const ONLINE_PRESET_ORDER = Object.keys(ONLINE_PRESET_LABELS);

// The one dropdown value that is not a preset. Exact dates are the rare case,
// so they stay behind it rather than taking up a filter slot of their own.
const ONLINE_CUSTOM = 'CUSTOM';

// What the column and this filter both mean, which is not what either name
// suggests: the field syncs from Salesforce `Stocking_Date__c`, and the install
// date is a separate field that does not reach CMS.
const ONLINE_DATE_HINT =
  "Online date is the location's stocking date, synced from Salesforce";

// Which group each filter is shown under. Location = facts about the site, from
// fetchFridgeLocations; Health = what the evaluator says now, from
// getFleetStatus. Grouping by data source means a new filter's home is decided
// by which fetch supplies it, not by taste.
const LOCATION_FILTERS = ['region', 'market', 'stockingStatus'];
const HEALTH_FILTERS = [
  'status',
  'jofemarState',
  'outOfOrder',
  'safetyHold',
  'peripheralDown',
  'reason',
];

// Chip name per filter key. A key absent here renders no chip, so a new filter
// has to be added deliberately rather than going silently unrepresented.
const FILTER_CHIP_LABELS = {
  status: 'Status',
  region: 'Region',
  market: 'Market',
  accountTier: 'Tier',
  stockingStatus: 'Stocking',
  jofemarState: 'Jofemar',
  outOfOrder: 'Out of order',
  safetyHold: 'Safety hold',
  workOrders: 'Work orders',
  peripheralDown: 'Peripheral down',
  reason: 'Reason',
  search: 'Location',
};

// Raw filter values are storage keys, not display text.
const FILTER_CHIP_VALUES = {
  status: (value) => STATUS_LABELS[value] || value,
  outOfOrder: (value) => (value === 'yes' ? 'Yes' : 'No'),
  safetyHold: (value) => (value === 'yes' ? 'Yes' : 'No'),
  workOrders: (value) => (value === 'yes' ? 'Open' : 'None'),
  search: (value) => {
    const terms = searchTerms(value);
    return terms.length > 1 ? `${terms.length} IDs` : value;
  },
  peripheralDown: (value) =>
    PERIPHERAL_OPTIONS.find((o) => o.value === value)?.label || value,
};

/** Human reading of the online range — the preset name when one matches. */
function describeOnlineRange(from, to, presets) {
  const preset = ONLINE_PRESET_ORDER.find(
    (key) => presets[key].from === from && presets[key].to === to,
  );
  if (preset) return ONLINE_PRESET_LABELS[preset];
  if (from && to) return `${from} to ${to}`;
  return from ? `from ${from}` : `up to ${to}`;
}

/**
 * Every applied filter as a removable chip, whichever surface set it — the
 * summary cards and reason chips write the same state, so they appear here too.
 * @returns {Object[]} {id, label, patch} — patch clears that filter.
 */
function activeFilterChips(filters, presets) {
  const chips = [];
  Object.keys(FILTER_CHIP_LABELS).forEach((key) => {
    const format = FILTER_CHIP_VALUES[key];
    const name = FILTER_CHIP_LABELS[key];
    const show = (value) => (format ? format(value) : value);
    if (!MULTI_FILTERS.includes(key)) {
      if (filters[key] !== '') {
        chips.push({
          id: key,
          label: `${name}: ${show(filters[key])}`,
          patch: { [key]: '' },
        });
      }
      return;
    }
    // One chip per value, not one per filter: picking three statuses and
    // wanting to drop just one is the whole point of multi-select.
    filters[key].forEach((value) => {
      chips.push({
        id: `${key}:${value}`,
        label: `${name}: ${show(value)}`,
        patch: { [key]: filters[key].filter((v) => v !== value) },
      });
    });
  });
  // One chip from two keys: a date range is a single idea to the reader.
  if (filters.onlineFrom || filters.onlineTo) {
    chips.push({
      id: 'online',
      label: `Online: ${describeOnlineRange(
        filters.onlineFrom,
        filters.onlineTo,
        presets,
      )}`,
      patch: { onlineFrom: '', onlineTo: '' },
    });
  }
  return chips;
}

// Column sort accessors. Module scope keeps the sort memo's deps stable.
const SORT_ACCESSORS = {
  location: (r) => r.locationId,
  status: (r) => r.status,
  lastHeartbeat: (r) => r.lastHeartbeat || 0,
  region: (r) => r.region || '',
  market: (r) => r.market || '',
  accountTier: (r) => r.accountTier || '',
  onlineDate: (r) => r.onlineDate || '',
  stockingStatus: (r) => r.stockingStatus || '',
  temperature: (r) => Number.parseFloat(r.temperature) || 0,
  jofemarState: (r) => r.jofemarState || '',
  // -1, not 0: "we don't know" must not sort alongside a clean record.
  dropouts: (r) => r.history?.longGapCount ?? -1,
  workOrders: (r) => r.activeWorkOrders?.length ?? -1,
};

// Sortable column keys, so a stale link cannot select an ordering the table
// no longer offers. 'problems' is the default and is handled separately.
const SORT_KEYS = Object.keys(SORT_ACCESSORS);

const nowSeconds = () => Math.floor(Date.now() / 1000);

const SEARCH_DEBOUNCE_MS = 300;
// Rows rendered per page. All matching rows are still counted, filtered and
// exported — this caps DOM size, which is what makes the first paint slow.
const PAGE_SIZE = 50;

// An unlisted status would hit `undefined + 1` -> NaN, which spreads to every
// card, pct and the sort. Insurance against the GraphQL enum growing.
const KNOWN_STATUSES = new Set(Object.values(FLEET_STATUS));

/** Backend verdict, or NO_DATA when there's no card or no `status` on it. */
function resolveStatus(card) {
  return KNOWN_STATUSES.has(card?.status) ? card.status : FLEET_STATUS.NO_DATA;
}

// Heartbeat staleness → color, so connectivity reads at a glance.
const HB_FRESH_SECONDS = 15 * 60; // matches the backend ALIVE_THRESHOLD
const HB_STALE_SECONDS = 24 * 60 * 60;
function heartbeatColor(lastHeartbeat, now) {
  if (!lastHeartbeat) return theme.colors.danger;
  const age = now - lastHeartbeat;
  if (age <= HB_FRESH_SECONDS) return theme.colors.primary;
  if (age <= HB_STALE_SECONDS) return theme.colors.warning;
  return theme.colors.danger;
}

const COPIED_FEEDBACK_MS = 1500;

// Click-to-copy the locationId — the string people paste into Slack and Jira.
function LocationIdButton({ locationId }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  // A row can be filtered out from under a pending timer; setting state warns.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(locationId);
    } catch (e) {
      // Non-secure context or denied permission — don't fake success.
      console.error('Clipboard write failed', e);
      return;
    }
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <button
      type="button"
      className={`fs-loc-id${copied ? ' fs-loc-id--copied' : ''}`}
      onClick={handleCopy}
      // Visible text is just the id, so the verb lives here — and this doubles
      // as the post-copy confirmation for screen readers.
      aria-label={copied ? `${locationId} copied` : `Copy ${locationId}`}
      title={copied ? 'Copied' : `Copy ${locationId}`}
    >
      {locationId}
      <span className="fs-loc-copy" aria-hidden="true">
        {copied ? <CheckIcon /> : <CopyIcon />}
      </span>
    </button>
  );
}

LocationIdButton.propTypes = {
  locationId: PropTypes.string.isRequired,
};

function StatusPill({ status }) {
  const color = STATUS_COLORS[status] || theme.colors.textMuted;
  return (
    <span className="fs-pill" style={{ color, background: `${color}1f` }}>
      <span className="fs-dot" style={{ background: color }} />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function renderPeripherals(row) {
  const down = peripheralsDown(row);
  // No vitals at all: say so. "All OK" would be a claim we can't make, and four
  // red chips would be a fault report we invented.
  if (down === null) return <span className="fs-unknown">—</span>;
  if (down.length === 0) return <span className="fs-ok">All OK</span>;
  return down.map((label) => (
    <span key={label} className="fs-chip">
      {label}
    </span>
  ));
}

function renderFlags(row) {
  const flags = [
    row.outOfOrder && 'Out of order',
    row.safetyHold && 'Safety hold',
  ].filter(Boolean);
  if (flags.length === 0 && row.ipConfigured) {
    return <span className="fs-ok">—</span>;
  }
  return (
    <>
      {flags.map((flag) => (
        <span key={flag} className="fs-chip">
          {flag}
        </span>
      ))}
      {/* Violet, not the red `fs-chip`: an unconfigured install explains why
          there is no reading rather than being a fault to go and fix. */}
      {!row.ipConfigured && (
        <span className="fs-install-chip fs-install-chip--new">No IP set</span>
      )}
    </>
  );
}

export default function FleetStatus() {
  const [isLoading, setIsLoading] = useState(true);
  const [fridges, setFridges] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  // A throttled read returns some cards plus an error; a silently short list
  // would make a struggling fleet look like a healthy smaller one.
  const [isPartial, setIsPartial] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const history = useHistory();
  const location = useLocation();
  // Read the URL once, on mount. After this the URL is an output, not an
  // input — re-reading it would fight with the writes below.
  const [initialFilters] = useState(() =>
    searchToFilters(location.search, localDateString()),
  );
  const [filters, setFilters] = useState(initialFilters);
  // Separate from `filters` so typing stays instant; pushed in on a debounce
  // because each change re-filters and re-sorts the whole fleet.
  const [searchInput, setSearchInput] = useState(initialFilters.search);
  // Default "problems first"; any column header switches to its own sort.
  const [sort, setSort] = useState(() =>
    searchToSort(location.search, SORT_KEYS),
  );
  const [explainerOpen, setExplainerOpen] = useState(false);

  const setFilter = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((prev) => ({ ...prev, search: searchInput })),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const search = filtersToSearch(filters, localDateString(), sort);
    // replace, not push: each keystroke in the search box would otherwise add a
    // history entry and Back would step through them one character at a time.
    history.replace({
      pathname: history.location.pathname,
      search: search ? `?${search}` : '',
    });
  }, [filters, sort, history]);

  // UI-only: the dropdown's Custom entry before any date is typed. Not a
  // filter, so it stays out of `filters` and out of the URL.
  const [customOnlinePicked, setCustomOnlinePicked] = useState(false);

  const loadFleet = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    setIsPartial(false);
    try {
      // Filtered here, not in the shared helper, so PortfolioHealth is unaffected.
      const liveProd = (await fetchFridgeLocations()).filter(
        (loc) => loc.status === 'LIVE' && loc.prod === 1,
      );
      // Placeholder-IP fridges stay in: they are LIVE and allocated, so leaving
      // them out understated the fleet. They carry a flag instead — see
      // `ipConfigured` below.
      const locations = liveProd;
      const ids = locations.map((loc) => loc.id);

      // One card per fridge: status, vitals and sparkline in one payload.
      // Chunking, dedupe and partial reads live in libs/fleetStatus.js.
      const { byId, partial } = await fetchFleetStatus(ids);

      const now = nowSeconds();
      const merged = locations.map((loc) => {
        // By locationId, never index — the response omits fridges with no card.
        const card = byId[loc.id] || null;
        // An install-time placeholder hostname, or no IP at all — either way
        // there is no address to reach, so nothing about this fridge can be
        // measured. A stale cache missing `ip` would make every fridge look
        // unconfigured, which is what the cache probe in fridgeLocations
        // guards against.
        const ipConfigured =
          typeof loc.ip === 'string' &&
          loc.ip !== '' &&
          !isPlaceholderIp(loc.ip);
        const reported = resolveStatus(card);
        return {
          locationId: loc.id,
          prettyName: loc.prettyName || loc.id,
          region: loc.region || '',
          market: loc.market || '',
          onlineDate: loc.onlineDate || '',
          ipConfigured,
          // From the roster, not the card — the only thing we can say about a
          // fridge that has no card at all.
          stockingStatus: loc.stockingStatus || '',
          // The page's status. An evaluator that probes a fridge with no
          // address can only ever fail, so its verdict there describes a check
          // that had no target — NO_IP is the only honest reading.
          status: ipConfigured ? reported : FLEET_STATUS.NO_IP,
          // What the evaluator actually said, kept for the CSV so overriding it
          // above never loses the backend's own answer.
          reportedStatus: reported,
          lastHeartbeat: card?.timestamp || null,
          temperature: cleanVital(card?.temperature),
          // Tri-state, not coerced: `!!card?.hdmi` turned "no vitals" into "HDMI
          // down" for every fridge without a card. null = unknown.
          hdmi: card?.hdmi ?? null,
          elo: card?.elo ?? null,
          cardReader: card?.cardReader ?? null,
          electricalLayout: card?.electricalLayout ?? null,
          jofemarState: card?.jofemarState || '',
          outOfOrder: !!card?.outOfOrder,
          safetyHold: !!card?.safetyHold,
          reasons: card?.allReasons || [],
          history: card?.heartbeatHistory || null,
          // null, not '' or []: an empty list would claim "no open work orders"
          // for a card that never carried the attribute at all.
          accountTier: loc.accountTier ?? null,
          activeWorkOrders: card?.activeWorkOrders ?? null,
        };
      });

      setFridges(merged);
      setIsPartial(partial);
      setLastUpdated(now);
    } catch (e) {
      console.error('Failed to load fleet status', e);
      setLoadError(true);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    loadFleet();
  }, [loadFleet]);

  const counts = useMemo(() => {
    const base = {
      total: fridges.length,
      [FLEET_STATUS.UP]: 0,
      [FLEET_STATUS.DOWN]: 0,
      [FLEET_STATUS.INACCESSIBLE]: 0,
      [FLEET_STATUS.NO_DATA]: 0,
      [FLEET_STATUS.NO_IP]: 0,
    };
    fridges.forEach((f) => {
      base[f.status] += 1;
    });
    // Percentages are of fridges we can speak to — dividing by the whole fleet
    // would report "85% up" for a fleet whose evaluated half is entirely healthy.
    base.evaluated = ABSENT_STATUS_ORDER.reduce(
      (left, status) => left - base[status],
      base.total,
    );
    // One name for "in scope but unmeasured", so the subtitle and the note
    // can't quote two different totals for the same idea.
    base.absent = base.total - base.evaluated;
    return base;
  }, [fridges]);

  const pct = (n) =>
    counts.evaluated ? Math.round((n / counts.evaluated) * 100) : 0;

  // Build dropdown options from the values actually present in the data.
  const optionsFor = useCallback(
    (key) =>
      Array.from(new Set(fridges.map((f) => f[key]).filter(Boolean)))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    [fridges],
  );

  const reasonOptions = useMemo(
    () =>
      Array.from(new Set(fridges.flatMap((f) => f.reasons)))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({ value, label: value })),
    [fridges],
  );

  // Most common failure reasons across the fleet, for the quick-filter strip.
  const topReasons = useMemo(() => {
    const tally = {};
    fridges.forEach((f) =>
      f.reasons.forEach((reason) => {
        if (reason) tally[reason] = (tally[reason] || 0) + 1;
      }),
    );
    return Object.entries(tally)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [fridges]);

  // One reading for the whole render — per row it would build 2,200 Dates,
  // and two rows could straddle midnight.
  const today = localDateString();

  const onlinePresets = onlineDatePresets(today);
  // Each preset carries the dates it resolves to today, so the entry states
  // its own rule instead of leaving "before today" to be worked out. Presets
  // in time order; exact dates last, since they are the rare case.
  const onlineOptions = [
    ...ONLINE_PRESET_ORDER.map((key) => ({
      value: key,
      label: `${ONLINE_PRESET_LABELS[key]}: ${describeDateBounds(
        onlinePresets[key].from,
        onlinePresets[key].to,
      )}`,
    })),
    { value: ONLINE_CUSTOM, label: 'Custom range…' },
  ];
  // A chip reads as active when the range equals what it would set, so editing
  // a date by hand simply deselects it — no separate "which chip" state.
  const activeOnlinePreset = ONLINE_PRESET_ORDER.find(
    (key) =>
      onlinePresets[key].from === filters.onlineFrom &&
      onlinePresets[key].to === filters.onlineTo,
  );
  const setOnlineRange = (from, to) =>
    setFilters((prev) => ({ ...prev, onlineFrom: from, onlineTo: to }));

  // Which dropdown entry is showing. Derived from the dates wherever they can
  // say it, so a shared link with a hand-picked range opens on Custom with the
  // fields already visible. `customPicked` covers only the gap the dates cannot
  // express: Custom chosen, nothing typed yet.
  const onlineMode =
    activeOnlinePreset ||
    (filters.onlineFrom || filters.onlineTo || customOnlinePicked
      ? ONLINE_CUSTOM
      : '');
  const selectOnlineMode = (value) => {
    setCustomOnlinePicked(value === ONLINE_CUSTOM);
    // Custom starts blank: keeping the preset's dates would match it again and
    // snap the dropdown straight back to the preset the reader just left.
    if (value === ONLINE_CUSTOM || value === '') {
      setOnlineRange('', '');
      return;
    }
    setOnlineRange(onlinePresets[value].from, onlinePresets[value].to);
  };
  const onlineRangeError =
    filters.onlineFrom &&
    filters.onlineTo &&
    filters.onlineTo < filters.onlineFrom
      ? 'End date is before the start date.'
      : undefined;

  // Cards and reason chips add to a selection rather than replacing it, so
  // "Down plus Inaccessible" is two clicks.
  const toggleMulti = (key, value) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(value)
        ? prev[key].filter((v) => v !== value)
        : [...prev[key], value],
    }));

  const appliedChips = activeFilterChips(filters, onlinePresets);
  const clearChip = (chip) => {
    // The search box keeps its own state for debouncing, so clearing the
    // filter alone would leave the typed text sitting in the input.
    if (chip.id === 'search') setSearchInput('');
    setFilters((prev) => ({ ...prev, ...chip.patch }));
  };
  const clearAll = () => {
    setSearchInput('');
    setFilters(emptyFilters());
  };

  const passesBool = (filterVal, actual) =>
    filterVal === '' || (filterVal === 'yes') === !!actual;

  const filtered = useMemo(
    () =>
      fridges.filter((f) => {
        if (
          DIRECT_MULTI_FILTERS.some(
            (key) => filters[key].length && !filters[key].includes(f[key]),
          )
        ) {
          return false;
        }
        if (
          !matchesOnlineRange(
            f.onlineDate,
            filters.onlineFrom,
            filters.onlineTo,
          )
        ) {
          return false;
        }
        if (BOOL_FILTERS.some((key) => !passesBool(filters[key], f[key]))) {
          return false;
        }
        // Only peripherals we KNOW are down: `!f[field]` would also match null,
        // which is "no vitals". Several selected reads as "any of these down".
        if (
          filters.peripheralDown.length &&
          !filters.peripheralDown.some((field) => f[field] === false)
        ) {
          return false;
        }
        if (filters.workOrders) {
          const wos = f.activeWorkOrders;
          // null is "not loaded", which is neither "has open" nor "none open".
          if (wos === null) return false;
          if (wos.length > 0 !== (filters.workOrders === 'yes')) return false;
        }
        if (
          filters.reason.length &&
          !filters.reason.some((reason) => f.reasons.includes(reason))
        ) {
          return false;
        }
        if (
          filters.search &&
          !matchesSearch(
            f.locationId,
            f.prettyName,
            filters.search,
            filters.matchMode,
          )
        ) {
          return false;
        }
        return true;
      }),
    [fridges, filters],
  );

  const sorted = useMemo(() => {
    if (sort.key === 'problems') {
      // NO_DATA below UP: unevaluated isn't a confirmed problem, and this keeps
      // the real rows reachable. Use the Stocking column to triage within it.
      const priority = {
        [FLEET_STATUS.DOWN]: 0,
        [FLEET_STATUS.INACCESSIBLE]: 1,
        [FLEET_STATUS.UP]: 2,
        [FLEET_STATUS.NO_DATA]: 3,
        // Last: nothing here can be acted on from this page, only from the
        // location's own IP field.
        [FLEET_STATUS.NO_IP]: 4,
      };
      return [...filtered].sort((a, b) => {
        const diff = priority[a.status] - priority[b.status];
        if (diff !== 0) return diff;
        // within a group, stalest heartbeat first (longest down at the top)
        return (a.lastHeartbeat || 0) - (b.lastHeartbeat || 0);
      });
    }
    const accessor = SORT_ACCESSORS[sort.key];
    if (!accessor) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [filtered, sort]);

  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );

  const hasActiveFilters = appliedChips.length > 0;

  // Counted over every matching row, not just the rendered page — these numbers
  // describe the filter result, so paging must not change them.
  const visibleCounts = useMemo(() => {
    const base = {};
    sorted.forEach((f) => {
      base[f.status] = (base[f.status] || 0) + 1;
    });
    return base;
  }, [sorted]);

  // Mounting 2,200 rows is what makes the first paint slow. Reset on filter/sort
  // change, or you'd be on page 5 of a list you just reordered.
  const [renderLimit, setRenderLimit] = useState(PAGE_SIZE);
  // "Load all" mounts every remaining row in one commit, which pins the tab for
  // several seconds on a full fleet. This flag is what puts a spinner on screen
  // before that starts.
  const [isExpanding, setIsExpanding] = useState(false);
  useEffect(() => {
    setRenderLimit(PAGE_SIZE);
    setIsExpanding(false);
  }, [filters, sort]);
  const visibleRows = useMemo(
    () => sorted.slice(0, renderLimit),
    [sorted, renderLimit],
  );
  const hasMoreRows = sorted.length > visibleRows.length;

  const expandFrames = useRef([]);
  useEffect(
    () => () => expandFrames.current.forEach((id) => cancelAnimationFrame(id)),
    [],
  );
  const loadAllRows = useCallback(() => {
    const total = sorted.length;
    const schedule = (fn) =>
      expandFrames.current.push(requestAnimationFrame(fn));
    setIsExpanding(true);
    // Two frames, not one. React batches both state calls made in a click into
    // a single commit, so setting the flag and the limit together would paint
    // the spinner and the rows at the same moment — after the freeze, too late
    // to have meant anything. A frame callback also runs *before* its own frame
    // is painted, so the first rAF only guarantees the spinner is in the tree;
    // the second runs once it is actually on screen, and that is where the
    // blocking render belongs.
    schedule(() =>
      schedule(() => {
        setRenderLimit(total);
        setIsExpanding(false);
      }),
    );
  }, [sorted]);

  // One clock reading per render, or each row resolves a slightly different "now".
  const now = nowSeconds();

  // Declarative column config. `sortKey` matches a SORT_ACCESSORS entry.
  // Widths are percentages of the table, and must total 100 — they used to sum
  // to 119, which is what compressed every column. They resolve against the
  // table's min width, so a narrow viewport scrolls rather than squeezing.
  const columns = [
    {
      sortKey: 'location',
      label: 'Location',
      width: '11%',
      render: (r) => (
        <div className="fs-loc">
          <LocationIdButton locationId={r.locationId} />
          {r.prettyName && r.prettyName !== r.locationId && (
            <span className="fs-loc-name">{r.prettyName}</span>
          )}
          {/* New tab, not in-place: this page is expensive to rebuild, so following
              a link and coming back would mean a full refetch and losing filters. */}
          <span className="fs-loc-actions">
            <Link
              className="fs-minibtn fs-minibtn--timeline"
              to={`/fridge-health/timeline/${r.locationId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Event Timeline
            </Link>
            <Link
              className="fs-minibtn"
              to={`/locations/health/${r.locationId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Health
            </Link>
            <Link
              className="fs-minibtn fs-minibtn--detail"
              to={`/locations/detail/${r.locationId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Detail
            </Link>
            <Link
              className="fs-minibtn fs-minibtn--inventory"
              to={`/locations/inventory/${r.locationId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Inventory
            </Link>
          </span>
        </div>
      ),
    },
    {
      sortKey: 'status',
      label: 'Status',
      width: '10%',
      render: (r) => <StatusPill status={r.status} />,
    },
    // Sits next to Status deliberately: a "Not monitored" pill beside "Removed"
    // explains itself, and beside "Stocking" it's a genuine anomaly.
    {
      sortKey: 'stockingStatus',
      label: 'Stocking',
      width: '9%',
      render: (r) => r.stockingStatus || <span className="fs-unknown">—</span>,
    },
    // Priority context, not health: which tier the site is on.
    {
      sortKey: 'accountTier',
      label: 'Tier',
      width: '5%',
      render: (r) => r.accountTier || <span className="fs-unknown">—</span>,
    },
    // Beside Stocking: together these explain most "alarming but fine" rows —
    // a fridge with no heartbeat that nobody has installed yet.
    {
      sortKey: 'onlineDate',
      label: 'Online',
      width: '7%',
      render: (r) => {
        const age = installAge(r.onlineDate, today);
        return (
          <div className="fs-online">
            {r.onlineDate ? (
              r.onlineDate.slice(0, 10)
            ) : (
              <span className="fs-unknown">—</span>
            )}
            {/* Established has no class entry, so it renders no badge — it is
                the norm, and a badge on most rows is wallpaper. */}
            {INSTALL_BADGE_CLASS[age] && (
              <span className={INSTALL_BADGE_CLASS[age]}>
                {INSTALL_AGE_LABELS[age]}
              </span>
            )}
          </div>
        );
      },
    },
    {
      sortKey: null,
      label: 'Reasons',
      width: '14%',
      render: (r) =>
        r.reasons.length ? (
          <span className="fs-reasons">{r.reasons.join(', ')}</span>
        ) : (
          '—'
        ),
    },
    {
      sortKey: 'workOrders',
      label: 'Work orders',
      hint:
        'Every open eMaint work order, including work raised by ops that ' +
        'monitoring cannot see — so a fridge reading Up can still have one. ' +
        'Each number links to the ticket; hover for the description and who ' +
        'it is assigned to.',
      width: '8%',
      render: (r) => {
        // Three states, three renderings: not loaded, none open, some open.
        if (r.activeWorkOrders === null) {
          return <span className="fs-unknown">—</span>;
        }
        if (r.activeWorkOrders.length === 0) {
          return <span className="fs-ok">None</span>;
        }
        return (
          <span className="fs-wos">
            {r.activeWorkOrders.map((wo, i) => {
              const url = emaintWorkOrderUrl(wo.wo);
              const opened = workOrderOpenedAt(wo.date_wo);
              const desc = workOrderShortDesc(wo.brief_desc, r.locationId);
              const title = [desc, wo.assignto && `Assigned: ${wo.assignto}`]
                .filter(Boolean)
                .join('\n');
              return (
                // A row with no number still counts, so it is shown rather than
                // dropped — it just cannot be linked.
                // eslint-disable-next-line react/no-array-index-key
                <span key={wo.wo || `wo-${i}`} className="fs-wo-item">
                  <span className="fs-wo-top">
                    {url ? (
                      <a
                        className="fs-wo"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={title || undefined}
                      >
                        {wo.wo}
                      </a>
                    ) : (
                      <span className="fs-unknown">no number</span>
                    )}
                    {/* No age rather than a guessed one when date_wo is
                        unparseable — see workOrderOpenedAt. */}
                    {opened ? (
                      <span className="fs-wo-age">
                        {formatRelativeTime(opened, now)}
                      </span>
                    ) : (
                      <span className="fs-unknown">—</span>
                    )}
                  </span>
                  {desc && <span className="fs-wo-desc">{desc}</span>}
                </span>
              );
            })}
          </span>
        );
      },
    },
    {
      sortKey: 'lastHeartbeat',
      label: 'Heartbeat (last 24h)',
      width: '9%',
      render: (r) => (
        <div className="fs-hb">
          <span className="fs-hb-latest">
            <span className="fs-hb-latest-label">Latest</span>{' '}
            <span style={{ color: heartbeatColor(r.lastHeartbeat, now) }}>
              {formatRelativeTime(r.lastHeartbeat, now)}
            </span>
            {isHistoryStale(r.history, now) && (
              <>
                {' '}
                <span
                  className="fs-hb-stale"
                  title={`Sparkline last recomputed ${formatRelativeTime(
                    r.history.sparklineComputedAt,
                    now,
                  )} — it may not reflect the fridge's current state.`}
                >
                  strip stale
                </span>
              </>
            )}
          </span>
          <HeartbeatSparkline history={r.history} />
        </div>
      ),
    },
    {
      // Behaviour over the window, not current state: a fridge can read Up and
      // still have dropped off 30 times yesterday. Catches flapping units.
      sortKey: 'dropouts',
      label: 'Dropouts',
      hint:
        'Times this fridge went quiet for more than 15 minutes in the last 24h. ' +
        'Sort descending to find flapping fridges that currently read Up.',
      width: '5%',
      render: (r) =>
        r.history ? (
          <span
            className={
              r.history.longGapCount > 0
                ? 'fs-dropouts fs-dropouts--bad'
                : 'fs-dropouts'
            }
            title={
              r.history.longGapCount > 0
                ? `Worst single gap: ${formatGap(r.history.longestGapSeconds)}`
                : 'No gap longer than 15 minutes in this window.'
            }
          >
            {r.history.longGapCount}
          </span>
        ) : (
          '—'
        ),
    },
    {
      sortKey: 'temperature',
      label: 'Temp',
      width: '5%',
      render: (r) => r.temperature || '—',
    },
    {
      sortKey: null,
      label: 'Peripherals',
      width: '6%',
      render: renderPeripherals,
    },
    {
      sortKey: 'jofemarState',
      label: 'Jofemar',
      width: '6%',
      render: (r) => r.jofemarState || '—',
    },
    { sortKey: null, label: 'Flags', width: '5%', render: renderFlags },
  ];

  // Names the denominator, so nobody reads these as percentages of the fleet.
  const ofEvaluated = `of ${counts.evaluated} fridge${
    counts.evaluated === 1 ? '' : 's'
  } with a status row and an IP`;

  const summaryCards = [
    {
      key: '',
      // Not "Live fridges": LIVE is an admin flag, but the word reads as
      // "selling". This number's job is to be the other cards' denominator.
      label: 'Locations with status = LIVE + allocation = ON',
      value: counts.total,
      color: theme.colors.textDark,
    },
    ...EVALUATED_STATUS_ORDER.map((status) => ({
      key: status,
      label: `${STATUS_LABELS[status]} · ${pct(counts[status])}%`,
      value: counts[status],
      color: STATUS_COLORS[status],
      hint: `${STATUS_HINTS[status]} — ${ofEvaluated}.`,
    })),
    // No percentage: these sit outside the evaluated denominator, so a share of
    // it would be meaningless. Narrower than the rest, because neither is a
    // reading of the fleet's health — they are what is missing from it.
    ...ABSENT_STATUS_ORDER.map((status) => ({
      key: status,
      label: STATUS_LABELS[status],
      value: counts[status],
      color: STATUS_COLORS[status],
      hint: ABSENT_HINTS[status],
      minor: true,
    })),
  ];

  // react-select hands back an array of option objects, or null once the last
  // one is removed; the filter state stays plain strings either way.
  const renderMultiSelect = (key, label, placeholder, options) => (
    <div className="fs-field">
      <span className="fs-field-label" id={`fs-${key}-label`}>
        {label}
      </span>
      <MultiSelect
        options={options}
        placeholder={placeholder}
        value={options.filter((o) => filters[key].includes(o.value))}
        onChange={(selected) =>
          setFilter(
            key,
            (selected || []).map((o) => o.value),
          )
        }
        width={theme.sizes.filterFieldMinWidth}
        ariaLabelledBy={`fs-${key}-label`}
      />
    </div>
  );

  const renderSelect = (key, label, placeholder, options) => (
    <label className="fs-field" htmlFor={`fs-${key}`}>
      <span className="fs-field-label">{label}</span>
      <select
        id={`fs-${key}`}
        className="fs-control"
        value={filters[key]}
        onChange={(e) => setFilter(key, e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <StyledDiv>
      <div className="fs-header">
        <h1 className="fs-title">Fleet Status</h1>
        {/* Names the population and, when there is one, the gap in it — so the
            count is explained at the top rather than discovered halfway down. */}
        <div className="fs-subtitle">
          Fridge Health Monitoring for LIVE, allocated fridges.
          {counts.absent > 0 &&
            ` ${counts.absent} are in scope but cannot be measured.`}
        </div>
        <button
          type="button"
          className="fs-explainer-toggle"
          aria-expanded={explainerOpen}
          onClick={() => setExplainerOpen((v) => !v)}
        >
          How to read this page
          <span className="fs-explainer-caret" aria-hidden="true">
            {/* A stroked chevron, not a triangle glyph: those fill only a
                fraction of their em box and read as specks at text size. One
                icon, rotated by CSS off aria-expanded. */}
            <svg
              viewBox="0 0 19 19"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="4,7 9.5,13 15,7" />
            </svg>
          </span>
        </button>
        {explainerOpen && (
          /* One row of five: the three definitions plus the two cases people
             actually trip over. Wraps only when a column can no longer hold a
             readable line. */
          <div className="fs-explainer">
            <div className="fs-ex-col">
              <h3 className="fs-ex-h">This page</h3>
              <p>
                Each fridge checks in every ~5 mins by posting a heartbeat, its
                hardware/software vitals. Every 15 mins, Fridge Health
                Monitoring summarizes the last several heartbeats and marks each
                monitored fridge Up, Down or Inaccessible.
              </p>
              <p className="fs-ex-note">
                A status is a summary, not a live check — it can be a few
                minutes behind. The Heartbeat column shows each fridge&apos;s
                last check-in.
              </p>
            </div>
            <div className="fs-ex-col">
              <h3 className="fs-ex-h">eMaint</h3>
              <p>
                Repair tickets auto-created by fridge health monitoring or
                manually created by the ops team live in eMaint. Each monitored
                location&apos;s open work orders are listed in its health
                status, but eMaint is the source of truth for work orders.
              </p>
              <p className="fs-ex-note">
                {/* TODO(FT-3484): the column now says WHO opens tickets. What
                    is still unstated is what trips an auto-created one — add it
                    once ops confirms, rather than guessing. */}
                Check eMaint before assuming a fridge was missed or fixed.
              </p>
            </div>
            <div className="fs-ex-col">
              <h3 className="fs-ex-h">What cannot be measured</h3>
              <p>
                <strong>{STATUS_LABELS[FLEET_STATUS.NO_DATA]}</strong> — Fridge
                Health Monitoring is not watching these fridges, so this page
                cannot say whether they are healthy.
              </p>
              <p>
                <strong>{STATUS_LABELS[FLEET_STATUS.NO_IP]}</strong> — no
                address in CMS, so any check against them could only fail.
                Whatever the evaluator last wrote for one is in the CSV, under
                Reported by FHMS.
              </p>
              <p className="fs-ex-note">
                Both are left out of the Up / Down / Inaccessible percentages.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="fs-summary">
        {summaryCards.map((card) => (
          <button
            type="button"
            key={card.label}
            className={card.minor ? 'fs-card fs-card--minor' : 'fs-card'}
            title={card.hint}
            aria-pressed={
              card.key
                ? filters.status.includes(card.key)
                : !filters.status.length
            }
            // Additive: each card toggles its own status, so several can be
            // selected at once. The scope card has no status and clears them.
            onClick={() =>
              card.key
                ? toggleMulti('status', card.key)
                : setFilter('status', [])
            }
          >
            <p className="fs-card-count" style={{ color: card.color }}>
              {card.value}
            </p>
            <span className="fs-card-label">
              <span className="fs-dot" style={{ background: card.color }} />
              {card.label}
            </span>
          </button>
        ))}
      </div>

      {/* The three percentages share a denominator that is NOT the scope card:
          97% of 2,222 is ~106 fridges more than 97% of the evaluated set. Saying
          so here stops that subtraction being left to the reader. */}
      <p className="fs-summary-note">
        {`Up, Down and Inaccessible are shares of ${counts.evaluated} fridge${
          counts.evaluated === 1 ? '' : 's'
        } with a status row and an IP to reach`}
        {counts.absent > 0
          ? `, not of all ${counts.total} in scope. The other ${counts.absent} have no status row or no IP — see the last two cards.`
          : '.'}
      </p>

      {topReasons.length > 0 && (
        <div className="fs-reasons-strip">
          <span className="fs-reasons-strip-label">Top reasons</span>
          {topReasons.map(([reason, count]) => (
            <button
              type="button"
              key={reason}
              className="fs-reason-chip"
              aria-pressed={filters.reason.includes(reason)}
              // Toggles: clicking a selected chip removes just that reason, so
              // the chip that applied the filter can also undo it.
              onClick={() => toggleMulti('reason', reason)}
            >
              {reason} <strong>{count}</strong>
            </button>
          ))}
        </div>
      )}

      <div className="fs-filters">
        <div className="fs-applied">
          <span className="fs-group-label">
            {appliedChips.length > 0 ? 'Filtering by' : 'Showing'}
          </span>
          {appliedChips.length > 0 && (
            <span className="fs-applied-inner">
              {appliedChips.map((chip) => (
                <button
                  type="button"
                  key={chip.id}
                  className="fs-applied-chip"
                  onClick={() => clearChip(chip)}
                >
                  {chip.label}
                  <span className="fs-applied-x" aria-hidden="true">
                    ×
                  </span>
                  <span className="fs-sr-only">Remove filter</span>
                </button>
              ))}
            </span>
          )}
          {/* "match", not "shown": only a page of rows is rendered at a time. */}
          <span className="fs-applied-count">
            {hasActiveFilters
              ? `${sorted.length} of ${counts.total} fridges match`
              : `all ${counts.total} fridges`}
          </span>
          {/* Sits on the count, where a keyword quietly matching its own
              sub-locations ("ICNC" also pulling in ICNC_LEFT) becomes visible. */}
          {filters.search && (
            <button
              type="button"
              className="fs-reason-chip"
              aria-pressed={filters.matchMode === MATCH_EXACT}
              title="Match whole location IDs instead of any fridge containing the text"
              onClick={() =>
                setFilters((prev) => ({
                  ...prev,
                  matchMode: prev.matchMode === MATCH_EXACT ? '' : MATCH_EXACT,
                }))
              }
            >
              Show Exact ID matches only
            </button>
          )}
          {sorted.length > 0 &&
            Object.keys(STATUS_LABELS)
              .filter((status) => visibleCounts[status])
              .map((status) => (
                <span key={status} className="fs-meta-chip">
                  <span
                    className="fs-dot"
                    style={{ background: STATUS_COLORS[status] }}
                  />
                  {`${visibleCounts[status]} ${STATUS_LABELS[status]}`}
                </span>
              ))}
          {hasActiveFilters && (
            <button type="button" className="fs-clear" onClick={clearAll}>
              Clear all
            </button>
          )}
        </div>

        <div className="fs-search-row">
          <label className="fs-field" htmlFor="fs-search">
            <span className="fs-field-label">Search</span>
            <input
              id="fs-search"
              className="fs-control"
              type="text"
              value={searchInput}
              placeholder="ID or name, or paste a list…"
              aria-label="Filter by location ID or name; paste a list to match several"
              onChange={(e) => setSearchInput(e.target.value)}
              onPaste={(e) => {
                const text = e.clipboardData.getData('text');
                // Only intervene to flatten a multi-line paste onto one line;
                // spaces and commas already separate on their own.
                if (!/[\n\r]/.test(text)) return;
                e.preventDefault();
                const list = searchTerms(text).join(', ');
                const el = e.target;
                setSearchInput(
                  el.value.slice(0, el.selectionStart) +
                    list +
                    el.value.slice(el.selectionEnd),
                );
              }}
            />
          </label>
        </div>

        <div className="fs-group">
          <span className="fs-group-label">Location</span>
          <div className="fs-toolbar">
            {renderMultiSelect(
              'region',
              'Region',
              'All regions',
              optionsFor('region'),
            )}
            {renderMultiSelect(
              'market',
              'Market',
              'All markets',
              optionsFor('market'),
            )}
            {renderMultiSelect(
              'accountTier',
              'Tier',
              'All tiers',
              optionsFor('accountTier'),
            )}
            {renderMultiSelect(
              'stockingStatus',
              'Stocking',
              'Any stocking status',
              optionsFor('stockingStatus'),
            )}
            <div className="fs-field fs-online-filter">
              <label
                className="fs-field-label fs-field-label--defined"
                htmlFor="fs-online"
                title={ONLINE_DATE_HINT}
              >
                Online Date
              </label>
              {/* One row: the range belongs beside the entry that reveals it,
                  not under it, where it reads as a second filter. */}
              <div className="fs-online-controls">
                <select
                  id="fs-online"
                  className="fs-control"
                  value={onlineMode}
                  onChange={(e) => selectOnlineMode(e.target.value)}
                >
                  <option value="">Any date</option>
                  {onlineOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {onlineMode === ONLINE_CUSTOM && (
                  <DateRangeField
                    startValue={parseLocalDate(filters.onlineFrom)}
                    endValue={parseLocalDate(filters.onlineTo)}
                    onStartChange={(date) =>
                      setOnlineRange(
                        date ? localDateString(date) : '',
                        filters.onlineTo,
                      )
                    }
                    onEndChange={(date) =>
                      setOnlineRange(
                        filters.onlineFrom,
                        date ? localDateString(date) : '',
                      )
                    }
                    error={onlineRangeError}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="fs-group">
          <span className="fs-group-label">Health</span>
          <div className="fs-toolbar">
            {renderMultiSelect(
              'status',
              'Status',
              'All statuses',
              STATUS_OPTIONS,
            )}
            {renderMultiSelect('reason', 'Reason', 'Any reason', reasonOptions)}
            {renderMultiSelect(
              'jofemarState',
              'Jofemar',
              'Any state',
              optionsFor('jofemarState'),
            )}
            {renderSelect(
              'workOrders',
              'Work orders',
              'Any',
              WORK_ORDER_OPTIONS,
            )}
            {renderSelect('outOfOrder', 'Out of order', 'Any', BOOL_OPTIONS)}
            {renderSelect('safetyHold', 'Safety hold', 'Any', BOOL_OPTIONS)}
            {renderMultiSelect(
              'peripheralDown',
              'Peripheral down',
              'Any peripheral',
              PERIPHERAL_OPTIONS,
            )}
          </div>
        </div>
      </div>

      {loadError && (
        <div className="fs-notice fs-notice--error" role="alert">
          Couldn&apos;t load fleet status. The figures below may be out of date
          — try Refresh.
        </div>
      )}

      {isPartial && (
        <div className="fs-notice" role="status">
          Part of the fleet couldn&apos;t be read on this pass. Those fridges
          fall into &ldquo;{STATUS_LABELS[FLEET_STATUS.NO_DATA]}&rdquo; below
          rather than showing their real status, so the Up / Down / Inaccessible
          counts cover less of the fleet than usual. Refresh to retry.
        </div>
      )}

      <div className="fs-meta">
        <span>
          {lastUpdated
            ? `Updated ${formatRelativeTime(lastUpdated, now)}`
            : '—'}
        </span>
        <span className="fs-meta-spacer" />
        <button
          type="button"
          className="fs-csv"
          onClick={() =>
            downloadFleetStatusCsv(
              sorted,
              today,
              counts.total,
              // The same labels the chip row shows, so the file and the screen
              // describe the query identically.
              appliedChips.map((chip) => chip.label),
            )
          }
          disabled={sorted.length === 0}
          // Exports every matching row, not just the rendered page.
          title={`Download all ${sorted.length} matching row${
            sorted.length === 1 ? '' : 's'
          }, in the current sort order`}
        >
          Download CSV
        </button>
        <button
          type="button"
          className="fs-refresh"
          onClick={loadFleet}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <FleetHeartbeatTrend fridges={filtered} />

      {isLoading && fridges.length === 0 ? (
        <Loader />
      ) : (
        <div className="fs-tablewrap">
          <table className="fs-table">
            <colgroup>
              {columns.map((col) => (
                <col
                  key={col.label || 'actions'}
                  style={{ width: col.width }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.label || 'actions'} title={col.hint}>
                    {col.sortKey ? (
                      <button
                        type="button"
                        className="fs-sort"
                        onClick={() => toggleSort(col.sortKey)}
                        aria-label={`Sort by ${col.label}`}
                      >
                        {col.label}
                        {sort.key === col.sortKey ? (
                          <span className="fs-sort-arrow fs-sort-arrow--active">
                            {sort.dir === 'asc' ? '▲' : '▼'}
                          </span>
                        ) : (
                          <span className="fs-sort-arrow">↕</span>
                        )}
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((f) => (
                <tr key={f.locationId}>
                  {columns.map((col) => (
                    <td key={`${f.locationId}-${col.label || 'actions'}`}>
                      {col.render(f)}
                    </td>
                  ))}
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr>
                  <td className="fs-empty" colSpan={columns.length}>
                    No fridges match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {hasMoreRows && (
            <div className="fs-loadmore">
              <span>{`Showing ${visibleRows.length} of ${sorted.length}`}</span>
              <button
                type="button"
                className="fs-csv"
                onClick={() => setRenderLimit((n) => n + PAGE_SIZE)}
                disabled={isExpanding}
              >
                {`Load ${Math.min(
                  PAGE_SIZE,
                  sorted.length - visibleRows.length,
                )} more`}
              </button>
              <button
                type="button"
                className="fs-csv"
                onClick={loadAllRows}
                disabled={isExpanding}
              >
                Load all
              </button>
              {isExpanding && (
                <span className="fs-expanding" role="status">
                  <span className="fs-spinner" aria-hidden="true" />
                  {`Rendering ${sorted.length} rows…`}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </StyledDiv>
  );
}
